import type {
  AudioChunk,
  AudioChunkUploadState,
  EntityId,
  ISODateTime,
  Milliseconds,
} from "../domain/types.js";

export interface RecordingChunkInput {
  readonly recordingId: EntityId;
  readonly sequence: number;
  readonly startMs: Milliseconds;
  readonly endMs: Milliseconds;
  readonly mimeType: string;
  readonly bytes: ArrayBuffer | ArrayBufferView;
  readonly capturedAt?: ISODateTime;
}

export interface BufferedAudioChunk extends AudioChunk {
  readonly bytes: ArrayBuffer;
}

interface EncryptedBufferedAudioChunk extends AudioChunk {
  readonly encryption: "AES-GCM";
  readonly iv: ArrayBuffer;
  readonly ciphertext: ArrayBuffer;
}

export interface SequenceGap {
  readonly fromSequence: number;
  readonly toSequence: number;
}

export interface RecordingBufferStatus {
  readonly recordingId: EntityId;
  readonly chunkCount: number;
  readonly totalBytes: number;
  readonly pendingUploadCount: number;
  readonly safeThroughMs: Milliseconds;
  readonly sequenceGaps: readonly SequenceGap[];
}

export type RecordingStorageErrorCode =
  | "quota_exceeded"
  | "database_closed"
  | "transaction_aborted"
  | "crypto_failure"
  | "unknown";

export interface RecordingBufferHealth {
  readonly mode: "encrypted-indexeddb" | "memory";
  readonly degraded: boolean;
  readonly errorCode?: RecordingStorageErrorCode;
}

export function classifyRecordingStorageError(
  cause: unknown,
): RecordingStorageErrorCode {
  const candidate =
    cause && typeof cause === "object"
      ? (cause as { name?: unknown; message?: unknown })
      : null;
  const name = typeof candidate?.name === "string" ? candidate.name : "";
  const message =
    typeof candidate?.message === "string"
      ? candidate.message
      : cause instanceof Error
        ? cause.message
        : "";
  const detail = `${name} ${message}`;
  if (/QuotaExceeded/i.test(detail)) return "quota_exceeded";
  if (/InvalidState|DatabaseClosed|closed database/i.test(detail)) {
    return "database_closed";
  }
  if (/AbortError|TransactionInactive|transaction.*abort/i.test(detail)) {
    return "transaction_aborted";
  }
  if (/OperationError|DataError|encrypt|decrypt|crypto/i.test(detail)) {
    return "crypto_failure";
  }
  return "unknown";
}

export interface RecordingBuffer {
  getHealth(): RecordingBufferHealth;
  putChunk(input: RecordingChunkInput): Promise<BufferedAudioChunk>;
  getChunk(
    recordingId: EntityId,
    sequence: number,
  ): Promise<BufferedAudioChunk | null>;
  listChunks(recordingId: EntityId): Promise<readonly BufferedAudioChunk[]>;
  markUploaded(
    recordingId: EntityId,
    sequence: number,
    uploadedAt: ISODateTime,
  ): Promise<BufferedAudioChunk>;
  getStatus(recordingId: EntityId): Promise<RecordingBufferStatus>;
  clearRecording(recordingId: EntityId): Promise<void>;
  pruneRecordings(keepRecordingIds: readonly EntityId[]): Promise<number>;
  close(): void;
}

export interface CreateRecordingBufferOptions {
  readonly databaseName?: string;
  readonly indexedDBFactory?: IDBFactory | null;
  readonly subtleCrypto?: SubtleCrypto;
}

function cloneArrayBuffer(source: ArrayBuffer | ArrayBufferView): ArrayBuffer {
  if (ArrayBuffer.isView(source)) {
    const copy = new Uint8Array(source.byteLength);
    copy.set(new Uint8Array(source.buffer, source.byteOffset, source.byteLength));
    return copy.buffer;
  }
  return source.slice(0);
}

function defaultSubtleCrypto(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("Web Crypto SubtleCrypto is unavailable.");
  return subtle;
}

export async function sha256Hex(
  source: ArrayBuffer | ArrayBufferView,
  subtleCrypto: SubtleCrypto = defaultSubtleCrypto(),
): Promise<string> {
  const bytes = cloneArrayBuffer(source);
  const digest = await subtleCrypto.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function validateInput(input: RecordingChunkInput): void {
  if (!input.recordingId.trim()) throw new Error("recordingId is required.");
  if (!Number.isInteger(input.sequence) || input.sequence < 0) {
    throw new Error("Audio chunk sequence must be a non-negative integer.");
  }
  if (
    !Number.isFinite(input.startMs) ||
    !Number.isFinite(input.endMs) ||
    input.startMs < 0 ||
    input.endMs < input.startMs
  ) {
    throw new Error("Audio chunk time range is invalid.");
  }
  if (!input.mimeType.trim()) throw new Error("Audio chunk mimeType is required.");
  if (input.bytes.byteLength === 0) {
    throw new Error("Audio chunk cannot be empty.");
  }
}

async function materialiseChunk(
  input: RecordingChunkInput,
  subtleCrypto: SubtleCrypto,
): Promise<BufferedAudioChunk> {
  validateInput(input);
  const bytes = cloneArrayBuffer(input.bytes);
  return {
    recordingId: input.recordingId,
    sequence: input.sequence,
    startMs: input.startMs,
    endMs: input.endMs,
    mimeType: input.mimeType,
    byteLength: bytes.byteLength,
    sha256: await sha256Hex(bytes, subtleCrypto),
    uploadState: "pending",
    capturedAt: input.capturedAt ?? new Date().toISOString(),
    bytes,
  };
}

function cloneChunk(chunk: BufferedAudioChunk): BufferedAudioChunk {
  return {
    ...chunk,
    bytes: chunk.bytes.slice(0),
  };
}

function sameImmutableChunk(
  first: Pick<
    BufferedAudioChunk,
    "sha256" | "startMs" | "endMs" | "mimeType" | "byteLength"
  >,
  second: Pick<
    BufferedAudioChunk,
    "sha256" | "startMs" | "endMs" | "mimeType" | "byteLength"
  >,
): boolean {
  return (
    first.sha256 === second.sha256 &&
    first.startMs === second.startMs &&
    first.endMs === second.endMs &&
    first.mimeType === second.mimeType &&
    first.byteLength === second.byteLength
  );
}

function bufferStatus(
  recordingId: EntityId,
  chunks: readonly BufferedAudioChunk[],
): RecordingBufferStatus {
  const sorted = [...chunks].sort((a, b) => a.sequence - b.sequence);
  const sequenceGaps: SequenceGap[] = [];

  let expectedSequence = 0;
  let safeThroughMs = 0;
  for (const chunk of sorted) {
    if (chunk.sequence > expectedSequence) {
      sequenceGaps.push({
        fromSequence: expectedSequence,
        toSequence: chunk.sequence - 1,
      });
    }
    if (chunk.sequence === expectedSequence) {
      safeThroughMs = Math.max(safeThroughMs, chunk.endMs);
      expectedSequence += 1;
    } else if (chunk.sequence > expectedSequence) {
      expectedSequence = chunk.sequence + 1;
    }
  }

  if (sequenceGaps.length > 0) {
    const firstGap = sequenceGaps[0];
    const preceding = sorted.find(
      (chunk) => chunk.sequence === firstGap.fromSequence - 1,
    );
    safeThroughMs = preceding?.endMs ?? 0;
  }

  return {
    recordingId,
    chunkCount: sorted.length,
    totalBytes: sorted.reduce((sum, chunk) => sum + chunk.byteLength, 0),
    pendingUploadCount: sorted.filter(
      (chunk) => chunk.uploadState !== "uploaded",
    ).length,
    safeThroughMs,
    sequenceGaps,
  };
}

export class MemoryRecordingBuffer implements RecordingBuffer {
  readonly #recordings = new Map<
    EntityId,
    Map<number, BufferedAudioChunk>
  >();
  readonly #subtleCrypto: SubtleCrypto;
  readonly #openingErrorCode?: RecordingStorageErrorCode;

  constructor(
    subtleCrypto: SubtleCrypto = defaultSubtleCrypto(),
    openingErrorCode?: RecordingStorageErrorCode,
  ) {
    this.#subtleCrypto = subtleCrypto;
    this.#openingErrorCode = openingErrorCode;
  }

  getHealth(): RecordingBufferHealth {
    return {
      mode: "memory",
      degraded: this.#openingErrorCode !== undefined,
      ...(this.#openingErrorCode ? { errorCode: this.#openingErrorCode } : {}),
    };
  }

  async putChunk(input: RecordingChunkInput): Promise<BufferedAudioChunk> {
    const chunk = await materialiseChunk(input, this.#subtleCrypto);
    const recording =
      this.#recordings.get(chunk.recordingId) ??
      new Map<number, BufferedAudioChunk>();
    const existing = recording.get(chunk.sequence);

    if (existing) {
      if (!sameImmutableChunk(existing, chunk)) {
        throw new Error(
          `Audio chunk ${chunk.recordingId}/${chunk.sequence} is immutable and already contains different data.`,
        );
      }
      return cloneChunk(existing);
    }

    recording.set(chunk.sequence, cloneChunk(chunk));
    this.#recordings.set(chunk.recordingId, recording);
    return cloneChunk(chunk);
  }

  async getChunk(
    recordingId: EntityId,
    sequence: number,
  ): Promise<BufferedAudioChunk | null> {
    const chunk = this.#recordings.get(recordingId)?.get(sequence);
    return chunk ? cloneChunk(chunk) : null;
  }

  async listChunks(
    recordingId: EntityId,
  ): Promise<readonly BufferedAudioChunk[]> {
    return Array.from(this.#recordings.get(recordingId)?.values() ?? [])
      .sort((a, b) => a.sequence - b.sequence)
      .map(cloneChunk);
  }

  async markUploaded(
    recordingId: EntityId,
    sequence: number,
    uploadedAt: ISODateTime,
  ): Promise<BufferedAudioChunk> {
    const recording = this.#recordings.get(recordingId);
    const chunk = recording?.get(sequence);
    if (!chunk) {
      throw new Error(`Audio chunk ${recordingId}/${sequence} was not found.`);
    }
    const uploaded: BufferedAudioChunk = {
      ...chunk,
      uploadState: "uploaded",
      uploadedAt,
    };
    recording?.set(sequence, uploaded);
    return cloneChunk(uploaded);
  }

  async getStatus(recordingId: EntityId): Promise<RecordingBufferStatus> {
    return bufferStatus(recordingId, await this.listChunks(recordingId));
  }

  async clearRecording(recordingId: EntityId): Promise<void> {
    this.#recordings.delete(recordingId);
  }

  async pruneRecordings(keepRecordingIds: readonly EntityId[]): Promise<number> {
    const keep = new Set(keepRecordingIds);
    let removed = 0;
    for (const recordingId of this.#recordings.keys()) {
      if (keep.has(recordingId)) continue;
      this.#recordings.delete(recordingId);
      removed += 1;
    }
    return removed;
  }

  close(): void {
    this.#recordings.clear();
  }
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed."));
  });
}

function transactionPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted."));
  });
}

const BUFFER_KEY_ID = "device-buffer-key";

async function encryptChunk(
  chunk: BufferedAudioChunk,
  key: CryptoKey,
  subtleCrypto: SubtleCrypto,
): Promise<EncryptedBufferedAudioChunk> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await subtleCrypto.encrypt(
    { name: "AES-GCM", iv },
    key,
    chunk.bytes,
  );
  const { bytes: _bytes, ...metadata } = chunk;
  return {
    ...metadata,
    encryption: "AES-GCM",
    iv: cloneArrayBuffer(iv),
    ciphertext: cloneArrayBuffer(ciphertext),
  };
}

async function decryptChunk(
  chunk: EncryptedBufferedAudioChunk,
  key: CryptoKey,
  subtleCrypto: SubtleCrypto,
): Promise<BufferedAudioChunk> {
  const bytes = await subtleCrypto.decrypt(
    { name: "AES-GCM", iv: chunk.iv },
    key,
    chunk.ciphertext,
  );
  const {
    encryption: _encryption,
    iv: _iv,
    ciphertext: _ciphertext,
    ...metadata
  } = chunk;
  return { ...metadata, bytes: cloneArrayBuffer(bytes) };
}

async function loadOrCreateBufferKey(
  database: IDBDatabase,
  subtleCrypto: SubtleCrypto,
): Promise<CryptoKey> {
  const readTransaction = database.transaction(
    IndexedDbRecordingBuffer.keyStore,
    "readonly",
  );
  const readCompleted = transactionPromise(readTransaction);
  const existing = (await requestPromise(
    readTransaction
      .objectStore(IndexedDbRecordingBuffer.keyStore)
      .get(BUFFER_KEY_ID),
  )) as CryptoKey | undefined;
  await readCompleted;
  if (existing) return existing;

  const generated = (await subtleCrypto.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  )) as CryptoKey;
  const writeTransaction = database.transaction(
    IndexedDbRecordingBuffer.keyStore,
    "readwrite",
  );
  const writeCompleted = transactionPromise(writeTransaction);
  await requestPromise(
    writeTransaction
      .objectStore(IndexedDbRecordingBuffer.keyStore)
      .put(generated, BUFFER_KEY_ID),
  );
  await writeCompleted;
  return generated;
}

export class IndexedDbRecordingBuffer implements RecordingBuffer {
  static readonly databaseVersion = 2;
  static readonly chunkStore = "audioChunks";
  static readonly recordingIndex = "byRecording";
  static readonly keyStore = "encryptionKeys";

  readonly #database: IDBDatabase;
  readonly #subtleCrypto: SubtleCrypto;
  readonly #encryptionKey: CryptoKey;

  private constructor(
    database: IDBDatabase,
    subtleCrypto: SubtleCrypto,
    encryptionKey: CryptoKey,
  ) {
    this.#database = database;
    this.#subtleCrypto = subtleCrypto;
    this.#encryptionKey = encryptionKey;
  }

  getHealth(): RecordingBufferHealth {
    return { mode: "encrypted-indexeddb", degraded: false };
  }

  static async open(options: {
    readonly databaseName?: string;
    readonly indexedDBFactory: IDBFactory;
    readonly subtleCrypto?: SubtleCrypto;
  }): Promise<IndexedDbRecordingBuffer> {
    const request = options.indexedDBFactory.open(
      options.databaseName ?? "dicta-recording-buffer",
      IndexedDbRecordingBuffer.databaseVersion,
    );
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(IndexedDbRecordingBuffer.chunkStore)) {
        const store = database.createObjectStore(
          IndexedDbRecordingBuffer.chunkStore,
          { keyPath: ["recordingId", "sequence"] },
        );
        store.createIndex(
          IndexedDbRecordingBuffer.recordingIndex,
          "recordingId",
          { unique: false },
        );
      }
      if (!database.objectStoreNames.contains(IndexedDbRecordingBuffer.keyStore)) {
        database.createObjectStore(IndexedDbRecordingBuffer.keyStore);
      }
    };
    const database = await requestPromise(request);
    const subtleCrypto = options.subtleCrypto ?? defaultSubtleCrypto();
    const encryptionKey = await loadOrCreateBufferKey(database, subtleCrypto);
    const buffer = new IndexedDbRecordingBuffer(
      database,
      subtleCrypto,
      encryptionKey,
    );
    await buffer.migrateLegacyPlaintext();
    return buffer;
  }

  private async migrateLegacyPlaintext(): Promise<void> {
    const readTransaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readonly",
    );
    const readCompleted = transactionPromise(readTransaction);
    const stored = (await requestPromise(
      readTransaction
        .objectStore(IndexedDbRecordingBuffer.chunkStore)
        .getAll(),
    )) as Array<BufferedAudioChunk | EncryptedBufferedAudioChunk>;
    await readCompleted;
    const legacy = stored.filter(
      (chunk): chunk is BufferedAudioChunk => "bytes" in chunk,
    );
    if (legacy.length === 0) return;
    const encrypted = await Promise.all(
      legacy.map((chunk) =>
        encryptChunk(chunk, this.#encryptionKey, this.#subtleCrypto),
      ),
    );
    const writeTransaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readwrite",
    );
    const writeCompleted = transactionPromise(writeTransaction);
    const store = writeTransaction.objectStore(
      IndexedDbRecordingBuffer.chunkStore,
    );
    for (const chunk of encrypted) store.put(chunk);
    await writeCompleted;
  }

  async putChunk(input: RecordingChunkInput): Promise<BufferedAudioChunk> {
    const chunk = await materialiseChunk(input, this.#subtleCrypto);
    const readTransaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readonly",
    );
    const readCompleted = transactionPromise(readTransaction);
    const store = readTransaction.objectStore(IndexedDbRecordingBuffer.chunkStore);
    const key: IDBValidKey = [chunk.recordingId, chunk.sequence];
    const existing = (await requestPromise(
      store.get(key),
    )) as EncryptedBufferedAudioChunk | undefined;
    await readCompleted;

    if (existing) {
      if (!sameImmutableChunk(existing, chunk)) {
        throw new Error(
          `Audio chunk ${chunk.recordingId}/${chunk.sequence} is immutable and already contains different data.`,
        );
      }
      return cloneChunk(
        await decryptChunk(existing, this.#encryptionKey, this.#subtleCrypto),
      );
    }

    const encrypted = await encryptChunk(
      chunk,
      this.#encryptionKey,
      this.#subtleCrypto,
    );
    const writeTransaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readwrite",
    );
    const writeCompleted = transactionPromise(writeTransaction);
    await requestPromise(
      writeTransaction
        .objectStore(IndexedDbRecordingBuffer.chunkStore)
        .add(encrypted),
    );
    await writeCompleted;
    return cloneChunk(chunk);
  }

  async getChunk(
    recordingId: EntityId,
    sequence: number,
  ): Promise<BufferedAudioChunk | null> {
    const transaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readonly",
    );
    const completed = transactionPromise(transaction);
    const store = transaction.objectStore(IndexedDbRecordingBuffer.chunkStore);
    const result = (await requestPromise(
      store.get([recordingId, sequence]),
    )) as EncryptedBufferedAudioChunk | undefined;
    await completed;
    return result
      ? cloneChunk(
          await decryptChunk(result, this.#encryptionKey, this.#subtleCrypto),
        )
      : null;
  }

  async listChunks(
    recordingId: EntityId,
  ): Promise<readonly BufferedAudioChunk[]> {
    const transaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readonly",
    );
    const completed = transactionPromise(transaction);
    const index = transaction
      .objectStore(IndexedDbRecordingBuffer.chunkStore)
      .index(IndexedDbRecordingBuffer.recordingIndex);
    const chunks = (await requestPromise(
      index.getAll(recordingId),
    )) as EncryptedBufferedAudioChunk[];
    await completed;
    const decrypted = await Promise.all(
      chunks
        .sort((a, b) => a.sequence - b.sequence)
        .map((chunk) =>
          decryptChunk(chunk, this.#encryptionKey, this.#subtleCrypto),
        ),
    );
    return decrypted.map(cloneChunk);
  }

  async markUploaded(
    recordingId: EntityId,
    sequence: number,
    uploadedAt: ISODateTime,
  ): Promise<BufferedAudioChunk> {
    const transaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readwrite",
    );
    const completed = transactionPromise(transaction);
    const store = transaction.objectStore(IndexedDbRecordingBuffer.chunkStore);
    const existing = (await requestPromise(
      store.get([recordingId, sequence]),
    )) as EncryptedBufferedAudioChunk | undefined;
    if (!existing) {
      await completed;
      throw new Error(`Audio chunk ${recordingId}/${sequence} was not found.`);
    }

    const uploaded: EncryptedBufferedAudioChunk = {
      ...existing,
      uploadState: "uploaded" as AudioChunkUploadState,
      uploadedAt,
    };
    await requestPromise(store.put(uploaded));
    await completed;
    return cloneChunk(
      await decryptChunk(uploaded, this.#encryptionKey, this.#subtleCrypto),
    );
  }

  async getStatus(recordingId: EntityId): Promise<RecordingBufferStatus> {
    return bufferStatus(recordingId, await this.listChunks(recordingId));
  }

  async clearRecording(recordingId: EntityId): Promise<void> {
    const transaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readwrite",
    );
    const completed = transactionPromise(transaction);
    const index = transaction
      .objectStore(IndexedDbRecordingBuffer.chunkStore)
      .index(IndexedDbRecordingBuffer.recordingIndex);
    const cursorRequest = index.openKeyCursor(recordingId);

    await new Promise<void>((resolve, reject) => {
      cursorRequest.onerror = () =>
        reject(cursorRequest.error ?? new Error("IndexedDB cursor failed."));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          resolve();
          return;
        }
        transaction
          .objectStore(IndexedDbRecordingBuffer.chunkStore)
          .delete(cursor.primaryKey);
        cursor.continue();
      };
    });
    await completed;
  }

  async pruneRecordings(keepRecordingIds: readonly EntityId[]): Promise<number> {
    const keep = new Set(keepRecordingIds);
    const transaction = this.#database.transaction(
      IndexedDbRecordingBuffer.chunkStore,
      "readwrite",
    );
    const completed = transactionPromise(transaction);
    const store = transaction.objectStore(IndexedDbRecordingBuffer.chunkStore);
    const cursorRequest = store.openKeyCursor();
    let removed = 0;

    await new Promise<void>((resolve, reject) => {
      cursorRequest.onerror = () =>
        reject(cursorRequest.error ?? new Error("IndexedDB cursor failed."));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          resolve();
          return;
        }
        const key = cursor.primaryKey;
        const recordingId = Array.isArray(key) ? key[0] : null;
        if (typeof recordingId === "string" && !keep.has(recordingId)) {
          store.delete(key);
          removed += 1;
        }
        cursor.continue();
      };
    });
    await completed;
    return removed;
  }

  close(): void {
    this.#database.close();
  }
}

export async function createRecordingBuffer(
  options: CreateRecordingBufferOptions = {},
): Promise<RecordingBuffer> {
  const factory =
    options.indexedDBFactory === undefined
      ? globalThis.indexedDB
      : options.indexedDBFactory;
  const subtleCrypto = options.subtleCrypto ?? defaultSubtleCrypto();

  if (factory) {
    try {
      return await IndexedDbRecordingBuffer.open({
        databaseName: options.databaseName,
        indexedDBFactory: factory,
        subtleCrypto,
      });
    } catch (cause) {
      // Browser privacy settings and quota policies can block IndexedDB.
      return new MemoryRecordingBuffer(
        subtleCrypto,
        classifyRecordingStorageError(cause),
      );
    }
  }

  return new MemoryRecordingBuffer(subtleCrypto);
}
